import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaEdit, FaSearch, FaPlus, FaFileAlt, FaUsers, FaStickyNote, FaCheckCircle, FaTimesCircle, FaUserCheck, FaExclamationTriangle, FaShoppingCart, FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import { SiQuickbooks } from 'react-icons/si';
import styles from '../ClientManagement.module.css';
import { clientService } from '../../services/clientService';
import { clientTypeService } from '../../services/clientTypeService';
import { clientTypeTagsService } from '../../services/clientTypeTagsService';
import { vwClientSummaryService, VWClientSummaryHelper } from '../../services/vwClientSummaryService';
import { ClientTypeTagManager } from '../../components/clientTypeTags';
import { ClientDocumentsModal, ClientContactsModal, NotesModal, QuickBooksAccountModal } from '../../components/ui';
import EditClientModal from '../../components/ui/EditClientModal';
import { useAccessToken } from '../../hooks/useAuth';
import { useAlert } from '../../context/AlertContext';

/**
 * Component for displaying client status badge
 * Now uses helper to handle various isActive formats (boolean, number, string)
 */
const ClientStatusBadge = ({ isActive }) => {
    // Convert isActive to boolean using the same logic as the helper
    const isActiveBoolean = (() => {
        if (isActive === undefined || isActive === null) return false;
        if (typeof isActive === 'boolean') return isActive;
        if (typeof isActive === 'number') return isActive === 1;
        if (typeof isActive === 'string') return isActive.toLowerCase() === 'true' || isActive === '1';
        return false;
    })();
    
    return (
        <span className={`${styles.status} ${isActiveBoolean ? styles.active : styles.inactive}`}>
            {isActiveBoolean ? 'Active' : 'Inactive'}
        </span>
    );
};

/**
 * Component for displaying billing contact alert
 * Shows warning when client has 0 billing contacts
 */
const BillingContactAlert = ({ client }) => {
    const billingContactCount = client.billingContactCount || 0;
    
    if (billingContactCount > 0) {
        return null;
    }
    
    return (
        <div className={styles.billingContactAlert} title="No billing contacts assigned">
            <FaExclamationTriangle className={styles.alertIcon} />
            <span className={styles.alertText}>No Billing Contact</span>
        </div>
    );
};

/**
 * Component for client info buttons (documents, contacts, notes, orders, and QuickBooks)
 * Now displays actual counts from VW_Client_Summary view including order information
 * Updated to use tabs with icons and text labels
 */
const ClientInfoButtons = ({ client, onViewDocuments, onViewContacts, onViewNotes, onViewQBO }) => {
    const documentCount = client.documentCount || 0;
    const contactCount = client.contactCount || 0;
    const noteCount = client.noteCount || 0;
    const totalOrderCount = client.totalOrderCount || 0;
    const activeOrderCount = client.activeOrderCount || 0;
    const hasQboLink = !!client.quickBooksCustomerId;
    
    return (
        <div className={styles.clientInfoButtons}>
            <button
                className={styles.infoTab}
                onClick={() => onViewDocuments && onViewDocuments(client)}
                title={`View ${documentCount} document${documentCount !== 1 ? 's' : ''}`}
            >
                <FaFileAlt className={styles.infoTabIcon} />
                <span className={styles.infoTabLabel}>Documents</span>
                <span className={styles.infoTabCount} style={{ background: documentCount > 0 ? '#4CAF50' : '#F44336' }}>
                    {documentCount}
                </span>
            </button>
            <button
                className={styles.infoTab}
                onClick={() => onViewContacts && onViewContacts(client)}
                title={`View ${contactCount} contact${contactCount !== 1 ? 's' : ''}`}
            >
                <FaUsers className={styles.infoTabIcon} />
                <span className={styles.infoTabLabel}>Contacts</span>
                <span className={styles.infoTabCount} style={{ background: contactCount > 0 ? '#4CAF50' : '#F44336' }}>
                    {contactCount}
                </span>
            </button>
            <button
                className={styles.infoTab}
                onClick={() => onViewNotes && onViewNotes(client)}
                title={`View ${noteCount} note${noteCount !== 1 ? 's' : ''}`}
            >
                <FaStickyNote className={styles.infoTabIcon} />
                <span className={styles.infoTabLabel}>Notes</span>
                <span className={styles.infoTabCount} style={{ background: noteCount > 0 ? '#4CAF50' : '#F44336' }}>
                    {noteCount}
                </span>
            </button>
            <button
                className={styles.infoTab}
                title={`Total Orders: ${totalOrderCount} | Active Orders: ${activeOrderCount}`}
            >
                <FaShoppingCart className={styles.infoTabIcon} />
                <span className={styles.infoTabLabel}>Orders</span>
                <div className={styles.orderCounts}>
                    <span className={styles.infoTabCount} style={{ background: activeOrderCount > 0 ? '#2196F3' : '#9E9E9E' }} title={`${activeOrderCount} active order${activeOrderCount !== 1 ? 's' : ''}`}>
                        {activeOrderCount}
                    </span>
                    <span className={styles.orderDivider}>/</span>
                    <span className={styles.infoTabCount} style={{ background: totalOrderCount > 0 ? '#4CAF50' : '#F44336' }} title={`${totalOrderCount} total order${totalOrderCount !== 1 ? 's' : ''}`}>
                        {totalOrderCount}
                    </span>
                </div>
            </button>
            <button
                className={styles.infoTab}
                onClick={() => onViewQBO && onViewQBO(client)}
                title={hasQboLink ? 'Linked to QuickBooks' : 'Not linked to QuickBooks'}
            >
                <SiQuickbooks className={styles.infoTabIcon} />
                <span className={styles.infoTabLabel}>QBO</span>
                {hasQboLink ? (
                    <span className={styles.infoTabCount} style={{ background: '#4CAF50' }} title="Linked to QuickBooks">
                        <FaCheckCircle />
                    </span>
                ) : (
                    <span className={styles.infoTabCount} title="Not linked to QuickBooks">
                        <FaTimesCircle />
                    </span>
                )}
            </button>
        </div>
    );
};

/**
 * Component for client action buttons
 * Now uses helper to properly check isActive status
 * Delete functionality removed per business requirements
 * Toggle status moved to edit form only
 */
const ClientActions = ({ client, onEdit, loading }) => {
    return (
        <div className={styles.clientActions}>
            <button
                className={styles.actionButtonIcon}
                onClick={() => onEdit(client)}
                disabled={loading}
                title="Edit client"
            >
                <FaEdit />
            </button>
        </div>
    );
};

/**
 * ClientList Component - Displays all clients with filtering and CRUD operations
 */
const ClientList = () => {
    const navigate = useNavigate();
    const { getBackendAccessToken } = useAccessToken();
    const { setErrorAlert, setInfoAlert, setIsModalOpen } = useAlert();

    // State management
    const [clients, setClients] = useState([]);
    const [filteredClients, setFilteredClients] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
    const [activeFilter, setActiveFilter] = useState('active'); // 'all', 'active', 'inactive' - Default to active only
    const [clientStats, setClientStats] = useState({ total: 0, active: 0, inactive: 0 });
    const [clientTypes, setClientTypes] = useState([]);
    const [selectedClientType, setSelectedClientType] = useState('all'); // 'all' or specific client type name
    const [sortBy, setSortBy] = useState('name'); // 'none', 'name', 'code', 'documentCount', 'totalOrderCount', 'activeOrderCount'
    
    // Pagination state
    const [currentPage, setCurrentPage] = useState(0);
    const [pageSize, setPageSize] = useState(20);
    const [totalPages, setTotalPages] = useState(0);
    const [totalElements, setTotalElements] = useState(0);
    
    // OPTIMIZATION: Store all client type assignments to avoid N+1 queries
    const [allClientTypeAssignments, setAllClientTypeAssignments] = useState({});

    // Edit modal state
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [editingClient, setEditingClient] = useState(null);

    // Document modal state
    const [documentsModalOpen, setDocumentsModalOpen] = useState(false);
    const [selectedClientForDocuments, setSelectedClientForDocuments] = useState(null);

    // Contacts modal state
    const [contactsModalOpen, setContactsModalOpen] = useState(false);
    const [selectedClientForContacts, setSelectedClientForContacts] = useState(null);

    // Notes modal state
    const [notesModalOpen, setNotesModalOpen] = useState(false);
    const [selectedClientForNotes, setSelectedClientForNotes] = useState(null);

    // QuickBooks modal state
    const [qboModalOpen, setQboModalOpen] = useState(false);
    const [selectedClientForQBO, setSelectedClientForQBO] = useState(null);

    /**
     * Debounce search term to avoid excessive API calls
     */
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearchTerm(searchTerm);
            setCurrentPage(0); // Reset to first page on new search
        }, 500);

        return () => clearTimeout(timer);
    }, [searchTerm]);

    /**
     * OPTIMIZED: Update client type assignments for a specific client without full reload
     * This updates the local clientTypesPreview to keep the UI in sync after tag changes
     */
    const updateClientTypeAssignments = useCallback(async (clientId, updatedAssignments = null) => {
        try {
            let clientAssignments = updatedAssignments;
            
            // If no assignments provided, fetch from API (fallback)
            if (!clientAssignments) {
                const accessToken = await getBackendAccessToken();
                clientAssignments = await clientTypeTagsService.getClientTypeAssignmentsWithDetails(accessToken, clientId);
            }
            
            setAllClientTypeAssignments(prev => ({
                ...prev,
                [clientId]: clientAssignments || []
            }));
            
            // Update the client's clientTypesPreview in the clients list
            setClients(prevClients => 
                prevClients.map(client => {
                    if (client.clientId === clientId) {
                        const assignmentNames = (clientAssignments || []).map(a => a.displayName || a.clientType).join(', ');
                        return {
                            ...client,
                            clientTypesPreview: assignmentNames
                        };
                    }
                    return client;
                })
            );
        } catch (error) {
            console.error('Failed to update client type assignments:', error);
            // On error, just show an error message - avoid triggering full reload
            setErrorAlert('Failed to update client tags. Please refresh the page if needed.');
        }
    }, [getBackendAccessToken, setErrorAlert]);

    /**
     * Save client type tag changes to the API
     */
    const saveClientTypeTags = useCallback(async (clientId, originalAssignments, newAssignments) => {
        try {
            const accessToken = await getBackendAccessToken();
            
            const originalIds = new Set(originalAssignments.map(a => a.clientTypeId));
            const newIds = new Set(newAssignments.map(a => a.clientTypeId));
            
            // Find tags to add (in new but not in original)
            const tagsToAdd = newAssignments.filter(a => !originalIds.has(a.clientTypeId));
            
            // Find tags to remove (in original but not in new)
            const tagsToRemove = originalAssignments.filter(a => !newIds.has(a.clientTypeId));
            
            // Add new tags
            for (const tag of tagsToAdd) {
                try {
                    await clientTypeTagsService.create(accessToken, {
                        clientId: clientId,
                        clientTypeId: tag.clientTypeId
                    });
                } catch (error) {
                    // Backend returns 400 if tag already exists or other validation errors
                    if (error.response?.status === 400) {
                        console.warn(`Failed to create tag for clientId ${clientId} and clientTypeId ${tag.clientTypeId}:`, error.response?.data);
                        console.warn('This might be because the tag already exists. Continuing...');
                        // Continue with other tags instead of failing completely
                    } else {
                        throw error; // Re-throw other errors
                    }
                }
            }
            
            // Remove old tags
            for (const tag of tagsToRemove) {
                // Find the assignment ID to delete
                const assignment = await clientTypeTagsService.getClientTypeAssignmentsWithDetails(accessToken, clientId);
                const toDelete = assignment.find(a => a.clientTypeId === tag.clientTypeId);
                if (toDelete && toDelete.clientTypeTagId) {
                    await clientTypeTagsService.delete(accessToken, toDelete.clientTypeTagId);
                }
            }
            
            // Update local state with the new assignments
            await updateClientTypeAssignments(clientId, newAssignments);
        } catch (error) {
            console.error('Failed to save client type tags:', error);
            throw error; // Re-throw to be caught by handleFormSubmit
        }
    }, [getBackendAccessToken, updateClientTypeAssignments]);

    /**
     * OPTIMIZED: Load clients using VW_Client_Summary view with pagination
     * This single call replaces the previous 3+ API calls (clients + tags + assignments)
     */
    const loadClients = useCallback(async () => {
        try {
            setLoading(true);
            const accessToken = await getBackendAccessToken();
            
            // Build sort string based on sortBy
            let sortString = 'clientName,asc';
            if (sortBy === 'code') {
                sortString = 'clientCode,asc';
            } else if (sortBy === 'documentCount') {
                sortString = 'documentCount,desc';
            } else if (sortBy === 'totalOrderCount') {
                sortString = 'totalOrderCount,desc';
            } else if (sortBy === 'activeOrderCount') {
                sortString = 'activeOrderCount,desc';
            }
            
            // Build pagination params
            const params = {
                page: currentPage,
                size: pageSize,
                sort: sortString
            };
            
            // OPTIMIZED: Use VW_Client_Summary view which includes clients, tags, and document counts
            // Also load client types and all assignments for tag management
            let clientDataResponse;
            
            // Choose appropriate paginated endpoint based on filters
            if (debouncedSearchTerm) {
                // Search takes precedence
                clientDataResponse = await vwClientSummaryService.searchClientsByNamePaged(accessToken, debouncedSearchTerm, params);
            } else if (selectedClientType !== 'all') {
                // Filter by client type
                clientDataResponse = await vwClientSummaryService.findClientsByTypePaged(accessToken, selectedClientType, params);
            } else {
                // Get all clients
                clientDataResponse = await vwClientSummaryService.getAllClientsPaged(accessToken, params);
            }
            
            // Extract paginated data
            const clientData = clientDataResponse.content || [];
            setTotalPages(clientDataResponse.totalPages || 0);
            setTotalElements(clientDataResponse.totalElements || 0);
            
            // Load client types and all assignments for tag management
            const [allAssignments, clientTypesData] = await Promise.all([
                clientTypeTagsService.getAll(accessToken),
                clientTypeService.getAll(accessToken)
            ]);
            
            // Create a lookup map for client types to enrich assignments
            const clientTypesMap = {};
            clientTypesData.forEach(clientType => {
                clientTypesMap[clientType.clientTypeId] = clientType;
            });
            
            // Group and enrich client type assignments by clientId
            const assignmentsByClientId = {};
            allAssignments.forEach(assignment => {
                if (!assignmentsByClientId[assignment.clientId]) {
                    assignmentsByClientId[assignment.clientId] = [];
                }
                
                // Enrich assignment with detailed client type information
                const enrichedAssignment = {
                    ...assignment,
                    clientType: clientTypesMap[assignment.clientTypeId]?.clientType || `Type ${assignment.clientTypeId}`,
                    clientTypeDescription: clientTypesMap[assignment.clientTypeId]?.clientTypeDescription || null,
                    displayName: clientTypesMap[assignment.clientTypeId]?.clientType || `Type ${assignment.clientTypeId}`,
                    displayDescription: clientTypesMap[assignment.clientTypeId]?.clientTypeDescription || null
                };
                
                assignmentsByClientId[assignment.clientId].push(enrichedAssignment);
            });
            
            setAllClientTypeAssignments(assignmentsByClientId);
            
            // Clean and process client data to remove any extra spaces
            // Note: VW_Client_Summary already includes clientTypesPreview, documentCount, contactCount, noteCount, and isActive
            const cleanedClientData = clientData.map(client => ({
                ...client,
                clientCode: client.clientCode ? client.clientCode.trim() : null,
                clientName: client.clientName ? client.clientName.trim() : null
            }));
            
            // Sorting is already handled by the API endpoint, no need to sort again
            setClients(cleanedClientData);
            
            // Apply local active/inactive filter to paginated results
            // BUT: When searching or filtering by type, show all results (don't apply active/inactive filter)
            // This allows users to find what they're looking for even if it's inactive
            let filtered = [...cleanedClientData];
            if (!debouncedSearchTerm && selectedClientType === 'all') {
                // Only apply active/inactive filter when not searching and not filtering by type
                if (activeFilter === 'active') {
                    filtered = filtered.filter(client => VWClientSummaryHelper.isClientActive(client));
                } else if (activeFilter === 'inactive') {
                    filtered = filtered.filter(client => !VWClientSummaryHelper.isClientActive(client));
                }
            }
            setFilteredClients(filtered);
        } catch (error) {
            console.error('Failed to load clients:', error);
            setErrorAlert('Failed to load clients. Please try again.');
            setClients([]);
            setFilteredClients([]);
            setTotalPages(0);
            setTotalElements(0);
        } finally {
            setLoading(false);
        }
    }, [sortBy, selectedClientType, debouncedSearchTerm, currentPage, pageSize, activeFilter, getBackendAccessToken, setErrorAlert]);

    /**
     * Load client statistics for filter buttons
     */
    const loadClientStats = useCallback(async () => {
        try {
            const accessToken = await getBackendAccessToken();
            const stats = await clientService.getStatistics(accessToken);
            setClientStats(stats);
        } catch (error) {
            console.error('Failed to load client statistics:', error);
            // Don't show error to user for stats, just use defaults
        }
    }, [getBackendAccessToken]);

    /**
     * Load all available client types from API (not just assigned ones)
     */
    const loadClientTypes = useCallback(async () => {
        try {
            const accessToken = await getBackendAccessToken();
            const allClientTypes = await clientTypeService.getAll(accessToken);
            setClientTypes(allClientTypes || []);
        } catch (error) {
            console.error('Failed to load client types:', error);
            setClientTypes([]);
            // Fallback: Extract unique types from already loaded client data
            try {
                const uniqueTypes = new Set();
                clients.forEach(client => {
                    if (client.clientTypesPreview) {
                        const types = client.clientTypesPreview.split(', ').map(t => t.trim());
                        types.forEach(type => uniqueTypes.add(type));
                    }
                });
                
                // Convert to format expected by dropdown
                const typeOptions = Array.from(uniqueTypes).map((typeName, index) => ({
                    clientTypeId: index + 1, // Temporary ID for compatibility
                    clientType: typeName
                }));
                
                setClientTypes(typeOptions);
            } catch (fallbackError) {
                console.error('Failed to extract client types as fallback:', fallbackError);
            }
        }
    }, [getBackendAccessToken, clients]);

    // Load clients and stats on component mount and when filters/pagination change
    useEffect(() => {
        loadClients();
    }, [loadClients]);

    // Load stats separately (only once on mount)
    useEffect(() => {
        loadClientStats();
    }, [loadClientStats]);

    // Load all available client types from API when component mounts
    useEffect(() => {
        loadClientTypes();
    }, [loadClientTypes]);

    // Track edit modal state for alert positioning and header blocking
    useEffect(() => {
        if (editModalOpen) {
            document.body.setAttribute('data-modal-open', 'true');
            setIsModalOpen(true);
        } else {
            document.body.removeAttribute('data-modal-open');
            setIsModalOpen(false);
        }
        return () => {
            document.body.removeAttribute('data-modal-open');
            setIsModalOpen(false);
        };
    }, [editModalOpen, setIsModalOpen]);

    /**
     * Handle search input change with debounced API search
     */
    const handleSearchChange = (e) => {
        setSearchTerm(e.target.value);
    };

    /**
     * Handle client type filter change
     */
    const handleClientTypeChange = (e) => {
        setSelectedClientType(e.target.value);
        setCurrentPage(0); // Reset to first page
    };

    /**
     * Handle sort change
     */
    const handleSortChange = (e) => {
        setSortBy(e.target.value);
        setCurrentPage(0); // Reset to first page
    };

    /**
     * Handle page navigation
     */
    const handlePageChange = (newPage) => {
        if (newPage >= 0 && newPage < totalPages) {
            setCurrentPage(newPage);
        }
    };

    /**
     * Handle page size change
     */
    const handlePageSizeChange = (e) => {
        setPageSize(parseInt(e.target.value, 10));
        setCurrentPage(0); // Reset to first page
    };

    /**
     * Clear all filters
     */
    const clearAllFilters = () => {
        setSearchTerm('');
        setSelectedClientType('all');
        setCurrentPage(0);
    };

    /**
     * Handle filter change
     */
    const handleFilterChange = useCallback((filter) => {
        setActiveFilter(filter);
        setSearchTerm(''); // Clear search when changing filter
        setSelectedClientType('all'); // Reset client type filter when changing active/inactive filter
        setCurrentPage(0); // Reset to first page
    }, []);

    /**
     * Handle edit client
     */
    const handleEditClient = (client) => {
        setEditingClient(client);
        setEditModalOpen(true);
    };

    /**
     * Handle client updated from edit modal
     */
    const handleClientUpdated = async (updatedClient, wasDeleted = false, shouldCloseModal = true) => {
        if (wasDeleted) {
            // Client was deleted, reload the clients list
            await loadClients();
        } else if (updatedClient) {
            // Client was updated, update local state and reload assignments
            setClients(prevClients => 
                prevClients.map(c => 
                    c.clientId === updatedClient.clientId ? updatedClient : c
                )
            );
            
            // Reload client type assignments for this client
            await updateClientTypeAssignments(updatedClient.clientId);
        }
        
        // Only close the modal if explicitly requested (default true for backward compatibility)
        if (shouldCloseModal) {
            setEditModalOpen(false);
            setEditingClient(null);
        }
    };

    /**
     * Handle close edit modal
     */
    const handleCloseEditModal = () => {
        setEditModalOpen(false);
        setEditingClient(null);
    };

    /**
     * Handle delete client
     * DISABLED: Delete functionality removed per business requirements
     * Kept here for potential future restoration if needed
     */
    /*
    const handleDeleteClient = async (client) => {
        const confirmMessage = `Are you sure you want to delete client "${client.clientName || client.clientCode}"? This action cannot be undone.`;
        
        if (!window.confirm(confirmMessage)) {
            return;
        }

        try {
            setLoading(true);
            const accessToken = await getBackendAccessToken();
            await clientService.delete(accessToken, client.clientId);
            setInfoAlert('Client deleted successfully');
            
            // OPTIMIZED: Update local data instead of reloading everything
            const updatedClients = clients.filter(c => c.clientId !== client.clientId);
            setClients(updatedClients);
            
            // Update stats locally using helper
            const newStats = {
                total: updatedClients.length,
                active: updatedClients.filter(c => VWClientSummaryHelper.isClientActive(c)).length,
                inactive: updatedClients.filter(c => !VWClientSummaryHelper.isClientActive(c)).length,
                recentClients: clientStats.recentClients // Keep existing count
            };
            setClientStats(newStats);
        } catch (error) {
            console.error('Failed to delete client:', error);
            setErrorAlert('Failed to delete client. Please try again.');
        } finally {
            setLoading(false);
        }
    };
    */

    /**
     * Handle viewing documents for a client
     */
    const handleViewDocuments = useCallback((client) => {
        setSelectedClientForDocuments(client);
        setDocumentsModalOpen(true);
    }, []);

    /**
     * Handle closing documents modal
     */
    const handleCloseDocumentsModal = useCallback(() => {
        setDocumentsModalOpen(false);
        setSelectedClientForDocuments(null);
    }, []);

    /**
     * Handle viewing contacts for a client
     */
    const handleViewContacts = useCallback((client) => {
        setSelectedClientForContacts(client);
        setContactsModalOpen(true);
    }, []);

    /**
     * Handle closing contacts modal
     */
    const handleCloseContactsModal = useCallback(() => {
        setContactsModalOpen(false);
        setSelectedClientForContacts(null);
    }, []);

    /**
     * Handle viewing notes for a client
     */
    const handleViewNotes = useCallback((client) => {
        setSelectedClientForNotes(client);
        setNotesModalOpen(true);
    }, []);

    /**
     * Handle closing notes modal
     */
    const handleCloseNotesModal = useCallback(() => {
        setNotesModalOpen(false);
        setSelectedClientForNotes(null);
    }, []);

    /**
     * Handle notes changed - reload client data to update note count
     */
    const handleNotesChanged = useCallback(() => {
        // Reload clients to get updated note count
        loadClients();
    }, [loadClients]);

    /**
     * Handle viewing QuickBooks account for a client
     */
    const handleViewQBO = useCallback((client) => {
        setSelectedClientForQBO(client);
        setQboModalOpen(true);
    }, []);

    /**
     * Handle closing QuickBooks modal
     */
    const handleCloseQBOModal = useCallback(() => {
        setQboModalOpen(false);
        setSelectedClientForQBO(null);
    }, []);

    return (
        <div className={styles.clientListSection}>
            <div className={styles.resultsHeader}>
                <h2>All Clients</h2>
                <button 
                    className={styles.backButton}
                    onClick={() => navigate('/clients')}
                >
                    ← Back to Client Management
                </button>
            </div>

            {/* Search and Filter Controls */}
            <div className={styles.searchSection}>
                <div className={styles.searchForm}>
                    <div className={styles.searchInputGroup}>
                        <input
                            type="text"
                            className={styles.searchInput}
                            placeholder="Search by client name or code..."
                            value={searchTerm}
                            onChange={handleSearchChange}
                        />
                        <button className={styles.searchButton} disabled>
                            <FaSearch />
                        </button>
                        <div className={styles.searchButtonSeparator}></div>
                        <button
                            className={styles.addButton}
                            onClick={() => navigate('/clients/onboard')}
                            disabled={loading}
                        >
                            <FaUserCheck /> Onboard Client
                        </button>
                    </div>
                    
                    {/* Filter Controls Row */}
                    <div className={styles.filterRow}>
                        {/* Client Type Filter Dropdown */}
                        <div className={styles.filterDropdown}>
                            <label htmlFor="clientTypeFilter" className={styles.filterLabel}>
                                Filter by Client Type:
                            </label>
                            <select
                                id="clientTypeFilter"
                                className={styles.filterSelect}
                                value={selectedClientType}
                                onChange={handleClientTypeChange}
                                disabled={loading}
                            >
                                <option value="all">All Client Types</option>
                                {clientTypes.map(type => (
                                    <option key={type.clientType} value={type.clientType}>
                                        {type.clientType}
                                    </option>
                                ))}
                            </select>
                        </div>

                        {/* Sort Controls */}
                        <div className={styles.filterDropdown}>
                            <label htmlFor="sortSelect" className={styles.filterLabel}>
                                Sort by:
                            </label>
                            <select
                                id="sortSelect"
                                className={styles.filterSelect}
                                value={sortBy}
                                onChange={handleSortChange}
                                disabled={loading}
                            >
                                <option value="name">Name</option>
                                <option value="code">Code</option>
                                <option value="documentCount">Document Count</option>
                                <option value="totalOrderCount">Total Orders</option>
                                <option value="activeOrderCount">Active Orders</option>
                            </select>
                        </div>
                        
                        {(searchTerm || selectedClientType !== 'all') && (
                            <button
                                className={styles.clearFiltersButton}
                                onClick={clearAllFilters}
                                disabled={loading}
                                title="Clear all filters"
                            >
                                Clear Filters
                            </button>
                        )}
                        
                        {/* Filter Buttons */}
                        <div className={styles.filterButtons}>
                            <button
                                className={`${styles.filterButton} ${activeFilter === 'all' ? styles.active : ''}`}
                                onClick={() => handleFilterChange('all')}
                                disabled={loading}
                            >
                                All Clients ({clientStats.total})
                            </button>
                            <button
                                className={`${styles.filterButton} ${activeFilter === 'active' ? styles.active : ''}`}
                                onClick={() => handleFilterChange('active')}
                                disabled={loading}
                            >
                                Active Only ({clientStats.active})
                            </button>
                            <button
                                className={`${styles.filterButton} ${activeFilter === 'inactive' ? styles.active : ''}`}
                                onClick={() => handleFilterChange('inactive')}
                                disabled={loading}
                            >
                                Inactive Only ({clientStats.inactive})
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Loading State */}
            {loading && (
                <div className={styles.loadingMessage}>
                    <div className={styles.spinner}></div>
                    Loading clients...
                </div>
            )}

            {/* Combined Pagination and Results Bar */}
            {!loading && filteredClients.length > 0 && (
                <div className={styles.paginationBar}>
                    {/* Left: Results Summary */}
                    <div className={styles.resultsSummary}>
                        Showing {filteredClients.length > 0 ? (currentPage * pageSize) + 1 : 0} - {Math.min((currentPage + 1) * pageSize, totalElements)} of {totalElements} clients
                    </div>

                    {/* Center: Pagination Controls */}
                    <div className={styles.paginationControls}>
                        <button
                            className={styles.pageButton}
                            onClick={() => handlePageChange(0)}
                            disabled={currentPage === 0 || totalPages <= 1}
                            title="First page"
                        >
                            First
                        </button>
                        
                        <button
                            className={styles.pageButton}
                            onClick={() => handlePageChange(currentPage - 1)}
                            disabled={currentPage === 0 || totalPages <= 1}
                            title="Previous page"
                        >
                            <FaChevronLeft /> Previous
                        </button>

                        <span className={styles.pageInfo}>
                            Page {currentPage + 1} of {totalPages || 1}
                        </span>

                        <button
                            className={styles.pageButton}
                            onClick={() => handlePageChange(currentPage + 1)}
                            disabled={currentPage >= totalPages - 1 || totalPages <= 1}
                            title="Next page"
                        >
                            Next <FaChevronRight />
                        </button>

                        <button
                            className={styles.pageButton}
                            onClick={() => handlePageChange(totalPages - 1)}
                            disabled={currentPage >= totalPages - 1 || totalPages <= 1}
                            title="Last page"
                        >
                            Last
                        </button>
                    </div>

                    {/* Right: Page Size Selector */}
                    <div className={styles.pageSizeSelector}>
                        <label>Items per page:</label>
                        <select value={pageSize} onChange={handlePageSizeChange}>
                            <option value={10}>10</option>
                            <option value={20}>20</option>
                            <option value={50}>50</option>
                            <option value={100}>100</option>
                        </select>
                    </div>
                </div>
            )}

            {/* Clients List */}
            {!loading && (
                <>
                    {filteredClients.length === 0 ? (
                        <div className={styles.noResults}>
                            {searchTerm || selectedClientType !== 'all' ? (
                                <>
                                    <p>No clients found matching your filter criteria:</p>
                                    <ul>
                                        {searchTerm && <li>Search term: "{searchTerm}"</li>}
                                        {selectedClientType !== 'all' && (
                                            <li>Client type: {selectedClientType}</li>
                                        )}
                                    </ul>
                                    <p>Try adjusting your filters or <button className={styles.clearFiltersLink} onClick={clearAllFilters}>clear all filters</button>.</p>
                                </>
                            ) : (
                                <p>No clients found</p>
                            )}
                        </div>
                    ) : (
                        <div className={styles.clientGrid}>
                            {filteredClients.map(client => (
                                <div key={client.clientId} className={styles.clientCard}>
                                    {/* Column 1: Client Name, Code, and Status */}
                                    <div className={styles.clientHeader}>
                                        <h3 title={client.clientName || 'Unnamed Client'}>
                                            {client.clientName || 'Unnamed Client'}
                                        </h3>
                                        <div className={styles.clientTitleGroup}>
                                            {client.clientCode && (
                                                <span className={styles.clientCode} title={client.clientCode}>
                                                    {client.clientCode}
                                                </span>
                                            )}
                                            <ClientStatusBadge isActive={client.isActive} />
                                        </div>
                                        {/* Billing Contact Alert */}
                                        <BillingContactAlert client={client} />
                                    </div>

                                    {/* Column 2: Info Buttons (Primary Focus) */}
                                    <ClientInfoButtons 
                                        client={client} 
                                        onViewDocuments={handleViewDocuments}
                                        onViewContacts={handleViewContacts}
                                        onViewNotes={handleViewNotes}
                                        onViewQBO={handleViewQBO}
                                    />

                                    {/* Column 3: Created Date */}
                                    <div className={styles.clientDetails}>
                                        {client.createdAt && (
                                            <p><strong>Created:</strong> {new Date(client.createdAt).toLocaleDateString()}</p>
                                        )}
                                    </div>

                            {/* Column 4: Client Type Tags (Secondary Focus) */}
                            <div className={styles.clientTypeTags}>
                                <div className={styles.clientTypeTagsLabel}>Client Tags</div>
                                <ClientTypeTagManager
                                    clientId={client.clientId}
                                    clientName={client.clientName}
                                    availableClientTypes={clientTypes}
                                    preloadedAssignments={allClientTypeAssignments[client.clientId] || []}
                                    size="small"
                                    maxVisible={3}
                                    compact={true}
                                    readOnly={true}
                                    // No onUpdate callback when readOnly - prevents unwanted API calls
                                />
                            </div>                                    {/* Column 5: Actions */}
                                    <ClientActions
                                        client={client}
                                        onEdit={handleEditClient}
                                        loading={loading}
                                    />
                                </div>
                            ))}
                        </div>
                    )}
                    
                    {/* Pagination Controls - Bottom */}
                    <div className={styles.pagination}>
                        <button
                            className={styles.pageButton}
                            onClick={() => handlePageChange(0)}
                            disabled={currentPage === 0 || totalPages <= 1}
                            title="First page"
                        >
                            First
                        </button>
                        
                        <button
                            className={styles.pageButton}
                            onClick={() => handlePageChange(currentPage - 1)}
                            disabled={currentPage === 0 || totalPages <= 1}
                            title="Previous page"
                        >
                            <FaChevronLeft /> Previous
                        </button>

                        <span className={styles.pageInfo}>
                            Page {currentPage + 1} of {totalPages || 1}
                        </span>

                        <button
                            className={styles.pageButton}
                            onClick={() => handlePageChange(currentPage + 1)}
                            disabled={currentPage >= totalPages - 1 || totalPages <= 1}
                            title="Next page"
                        >
                            Next <FaChevronRight />
                        </button>

                        <button
                            className={styles.pageButton}
                            onClick={() => handlePageChange(totalPages - 1)}
                            disabled={currentPage >= totalPages - 1 || totalPages <= 1}
                            title="Last page"
                        >
                            Last
                        </button>
                    </div>
                </>
            )}

            {/* Edit Client Modal */}
            <EditClientModal
                client={editingClient}
                isOpen={editModalOpen}
                onClose={handleCloseEditModal}
                onClientUpdated={handleClientUpdated}
                clientTypes={clientTypes}
                allClientTypeAssignments={allClientTypeAssignments}
            />

            {/* Client Documents Modal */}
            {documentsModalOpen && selectedClientForDocuments && (
                <ClientDocumentsModal
                    isOpen={documentsModalOpen}
                    onClose={handleCloseDocumentsModal}
                    clientId={selectedClientForDocuments.clientId}
                    clientName={selectedClientForDocuments.clientName || selectedClientForDocuments.clientCode || 'Client'}
                    clientCode={selectedClientForDocuments.clientCode}
                    onDataChanged={loadClients}
                />
            )}

            {/* Client Contacts Modal */}
            {contactsModalOpen && selectedClientForContacts && (
                <ClientContactsModal
                    isOpen={contactsModalOpen}
                    onClose={handleCloseContactsModal}
                    clientId={selectedClientForContacts.clientId}
                    clientName={selectedClientForContacts.clientName || selectedClientForContacts.clientCode || 'Client'}
                    clientCode={selectedClientForContacts.clientCode}
                    onDataChanged={loadClients}
                />
            )}

            {/* Notes Modal */}
            {notesModalOpen && selectedClientForNotes && (
                <NotesModal
                    isOpen={notesModalOpen}
                    onClose={handleCloseNotesModal}
                    ownerType="Client"
                    ownerId={selectedClientForNotes.clientId}
                    ownerName={selectedClientForNotes.clientName || selectedClientForNotes.clientCode || 'Client'}
                    ownerCode={selectedClientForNotes.clientCode}
                    onNotesChanged={handleNotesChanged}
                />
            )}

            {/* QuickBooks Account Modal */}
            {qboModalOpen && selectedClientForQBO && (
                <QuickBooksAccountModal
                    isOpen={qboModalOpen}
                    onClose={handleCloseQBOModal}
                    clientId={selectedClientForQBO.clientId}
                    clientName={selectedClientForQBO.clientName || selectedClientForQBO.clientCode || 'Client'}
                    clientCode={selectedClientForQBO.clientCode}
                    quickBooksCustomerId={selectedClientForQBO.quickBooksCustomerId}
                    onClientUpdated={loadClients}
                />
            )}
        </div>
    );
};

/**
 * Simple edit form component
 */
export default ClientList;
